import * as R from 'ramda';
import { BusinessPolicyType } from '../../../services/modules/business-policies/businessPolicies.types';
import {
  BusinessPolicyAstLogicalOperator,
  BusinessPolicyComparisonNodeDTO,
  BusinessPolicyConditionNodeDTO,
  BusinessPolicyGrain,
  BusinessPolicyLogicalNodeDTO,
  BusinessPolicyMeasureDTO,
  BusinessPolicyMeasureSource,
  BusinessPolicyNodeKind,
  BusinessPolicyScopeDTO,
  BusinessPolicyScopeDimension,
  BusinessPolicyUnmatchedHandling,
  BusinessPolicyZeroComparison,
} from '../../../services/modules/business-policies/businessPolicyCondition.types';
import { MEASURE_CAPABILITIES, findMeasureKey } from './businessPolicyCapabilities';
import {
  BusinessPolicyMeasureKey,
  BusinessPolicyMeasureSlot,
} from './businessPolicyCapabilities.types';
import {
  AppendConditionGroupParams,
  AppendCriterionToGroupParams,
  BusinessPolicyConditionGroup,
  BusinessPolicyNodeSlot,
  ConditionNodePath,
  CreateComparativeRootNodeParams,
  CreateSimpleRootNodeParams,
  NodeZeroComparisonParams,
  ReadNodeZeroComparisonParams,
  RemoveNodeScopeParams,
  SetNodeAtPathParams,
  SetNodeMeasureParams,
  SetNodeOrderHistoryParams,
  SetNodePriceCompositionsParams,
  UpsertNodeScopeParams,
} from './conditionTree.types';

const PERCENT_FACTOR = 100;
const PERCENT_PRECISION = 1e6;

export const toRightFactorPercent = (rightFactor: number): number =>
  Math.round(rightFactor * PERCENT_FACTOR * PERCENT_PRECISION) / PERCENT_PRECISION;

export const toRightFactor = (rightFactorPercent: number | null): number | null =>
  rightFactorPercent === null ? null : rightFactorPercent / PERCENT_FACTOR;

export const isComparisonNode = (
  node: BusinessPolicyConditionNodeDTO,
): node is BusinessPolicyComparisonNodeDTO => node.kind === BusinessPolicyNodeKind.COMPARISON;

export const stripConditionNodeIds = (
  node: BusinessPolicyConditionNodeDTO,
): BusinessPolicyConditionNodeDTO =>
  isComparisonNode(node)
    ? { ...node, id: undefined }
    : { ...node, id: undefined, children: node.children.map(stripConditionNodeIds) };

export const copyConditionTree = (
  node: BusinessPolicyConditionNodeDTO,
): BusinessPolicyConditionNodeDTO => stripConditionNodeIds(R.clone(node));

const RESTRICTIVE_TYPES: BusinessPolicyType[] = [
  BusinessPolicyType.ORDER_RESTRICTION,
  BusinessPolicyType.ORDER_WARNING,
  BusinessPolicyType.ORDER_PRODUCTS_RESTRICTION,
];

const COMPARISON_NODE_DEFAULTS = {
  zeroComparison: BusinessPolicyZeroComparison.EVALUATE,
  includeAbsentGroups: false,
  unmatchedHandling: BusinessPolicyUnmatchedHandling.BOTH,
};

const buildMeasure = (measureKey: BusinessPolicyMeasureKey): BusinessPolicyMeasureDTO => {
  const { fact, grain, source } = MEASURE_CAPABILITIES[measureKey];

  return { fact, grain, source, scopes: [], orderHistory: null, priceCompositionIds: [] };
};

export const createSimpleRootNode = ({
  measureKey,
  comparisonOperator,
}: CreateSimpleRootNodeParams): BusinessPolicyComparisonNodeDTO => ({
  kind: BusinessPolicyNodeKind.COMPARISON,
  comparisonOperator,
  left: buildMeasure(measureKey),
  rightConstant: null,
  rightMeasure: null,
  rightFactor: null,
  ...COMPARISON_NODE_DEFAULTS,
});

export const createComparativeRootNode = ({
  leftMeasureKey,
  rightMeasureKey,
  comparisonOperator,
}: CreateComparativeRootNodeParams): BusinessPolicyComparisonNodeDTO => ({
  kind: BusinessPolicyNodeKind.COMPARISON,
  comparisonOperator,
  left: buildMeasure(leftMeasureKey),
  rightConstant: null,
  rightMeasure: buildMeasure(rightMeasureKey),
  rightFactor: null,
  ...COMPARISON_NODE_DEFAULTS,
});

export const isComparativeNode = (node: BusinessPolicyConditionNodeDTO | null) =>
  !!node && isComparisonNode(node) && !!node.rightMeasure;

const createLogicalNode = (
  operator: BusinessPolicyAstLogicalOperator,
  children: BusinessPolicyConditionNodeDTO[],
): BusinessPolicyLogicalNodeDTO => ({
  kind: BusinessPolicyNodeKind.LOGICAL,
  operator,
  children,
});

const isAndOfComparisons = (node: BusinessPolicyConditionNodeDTO) =>
  node.kind === BusinessPolicyNodeKind.LOGICAL &&
  node.operator === BusinessPolicyAstLogicalOperator.AND &&
  node.children.length >= 2 &&
  node.children.every(isComparisonNode);

export const isDnfConditionTree = (root: BusinessPolicyConditionNodeDTO | null): boolean => {
  if (!root || isComparisonNode(root) || isAndOfComparisons(root)) {
    return true;
  }

  return (
    root.operator === BusinessPolicyAstLogicalOperator.OR &&
    root.children.length >= 2 &&
    root.children.every((child) => isComparisonNode(child) || isAndOfComparisons(child))
  );
};

const isRangeRootNode = (
  node: BusinessPolicyConditionNodeDTO,
): node is BusinessPolicyComparisonNodeDTO => isComparisonNode(node) && !node.rightMeasure;

export const getRangeConstant = (node: BusinessPolicyConditionNodeDTO | null) =>
  node && isRangeRootNode(node) ? node.rightConstant : null;

export const applyRangeConstant = (
  template: BusinessPolicyConditionNodeDTO | null,
  rightConstant: number | null,
): BusinessPolicyConditionNodeDTO | null => {
  if (!template) {
    return null;
  }

  const copy = copyConditionTree(template);

  return isRangeRootNode(copy) ? { ...copy, rightConstant } : copy;
};

const sortValues = <T extends string>(values: T[]) => [...values].sort();

const sortScopeValues = (scope: BusinessPolicyScopeDTO): BusinessPolicyScopeDTO => {
  switch (scope.dimension) {
    case BusinessPolicyScopeDimension.AVAILABILITY:
      return { ...scope, availabilities: sortValues(scope.availabilities) };
    case BusinessPolicyScopeDimension.DELIVERY_WINDOW:
      return { ...scope, deliveryWindowIds: sortValues(scope.deliveryWindowIds) };
    case BusinessPolicyScopeDimension.CLASSIFICATION:
      return { ...scope, classificationIds: sortValues(scope.classificationIds) };
    case BusinessPolicyScopeDimension.PRODUCT_COLOR:
      return { ...scope, productColorIds: sortValues(scope.productColorIds) };
    case BusinessPolicyScopeDimension.COLLECTION:
      return { ...scope, collectionIds: sortValues(scope.collectionIds) };
    case BusinessPolicyScopeDimension.ORDER_CREATION_DATE:
      return scope;
  }
};

const toComparableMeasure = (measure: BusinessPolicyMeasureDTO): BusinessPolicyMeasureDTO => ({
  ...measure,
  scopes: measure.scopes
    .map((scope) => ({ ...sortScopeValues(scope), references: undefined }))
    .sort((first, second) => first.dimension.localeCompare(second.dimension)),
  orderHistory: measure.orderHistory && {
    ...measure.orderHistory,
    orderStatuses: sortValues(measure.orderHistory.orderStatuses),
    synchronizedStatuses: sortValues(measure.orderHistory.synchronizedStatuses),
  },
  priceCompositionIds: sortValues(measure.priceCompositionIds),
});

const toComparableNode = (node: BusinessPolicyConditionNodeDTO): BusinessPolicyConditionNodeDTO =>
  isComparisonNode(node)
    ? {
        ...node,
        left: toComparableMeasure(node.left),
        rightMeasure: node.rightMeasure && toComparableMeasure(node.rightMeasure),
      }
    : { ...node, children: node.children.map(toComparableNode) };

export const toRangeTemplate = (node: BusinessPolicyConditionNodeDTO | null) => {
  const template = applyRangeConstant(node, null);

  return template && toComparableNode(template);
};

export const isRangeConditionList = (
  conditions: (BusinessPolicyConditionNodeDTO | null)[],
): boolean => {
  const [template = null, ...rest] = conditions.map(toRangeTemplate);

  if (template && !isComparisonNode(template)) {
    return false;
  }

  return rest.every((condition) => R.equals(condition, template));
};

export const getNodeAtPath = (
  root: BusinessPolicyConditionNodeDTO | null,
  path: ConditionNodePath,
): BusinessPolicyConditionNodeDTO | null =>
  path.reduce<BusinessPolicyConditionNodeDTO | null>(
    (node, index) =>
      node?.kind === BusinessPolicyNodeKind.LOGICAL ? (node.children[index] ?? null) : null,
    root,
  );

export const setNodeAtPath = ({
  root,
  path,
  node,
}: SetNodeAtPathParams): BusinessPolicyConditionNodeDTO => {
  if (!path.length) {
    return node;
  }

  if (isComparisonNode(root)) {
    return root;
  }

  const [index, ...rest] = path;

  return {
    ...root,
    children: root.children.map((child, childIndex) =>
      childIndex === index ? setNodeAtPath({ root: child, path: rest, node }) : child,
    ),
  };
};

export const listConditionGroups = (
  root: BusinessPolicyConditionNodeDTO | null,
): BusinessPolicyConditionGroup[] => {
  if (!root) {
    return [];
  }

  if (isComparisonNode(root)) {
    return [[{ node: root, path: [] }]];
  }

  if (root.operator !== BusinessPolicyAstLogicalOperator.OR) {
    return [root.children.map((node, index) => ({ node, path: [index] }))];
  }

  return root.children.map((child, groupIndex) =>
    isComparisonNode(child)
      ? [{ node: child, path: [groupIndex] }]
      : child.children.map((node, index) => ({ node, path: [groupIndex, index] })),
  );
};

export const listComparisonNodes = (
  root: BusinessPolicyConditionNodeDTO | null,
): BusinessPolicyComparisonNodeDTO[] => {
  if (!root) {
    return [];
  }

  if (isComparisonNode(root)) {
    return [root];
  }

  return root.children.flatMap((child) => listComparisonNodes(child));
};

export const mapConditionComparisons = (
  root: BusinessPolicyConditionNodeDTO,
  transform: (node: BusinessPolicyComparisonNodeDTO) => BusinessPolicyComparisonNodeDTO,
): BusinessPolicyConditionNodeDTO => {
  if (isComparisonNode(root)) {
    return transform(root);
  }

  return {
    ...root,
    children: root.children.map((child) => mapConditionComparisons(child, transform)),
  };
};

export const appendCriterionToGroup = ({
  root,
  groupIndex,
  criterion,
}: AppendCriterionToGroupParams): BusinessPolicyConditionNodeDTO => {
  if (isComparisonNode(root)) {
    return createLogicalNode(BusinessPolicyAstLogicalOperator.AND, [root, criterion]);
  }

  if (root.operator !== BusinessPolicyAstLogicalOperator.OR) {
    return { ...root, children: [...root.children, criterion] };
  }

  return {
    ...root,
    children: root.children.map((child, childIndex) => {
      if (childIndex !== groupIndex) {
        return child;
      }

      if (isComparisonNode(child)) {
        return createLogicalNode(BusinessPolicyAstLogicalOperator.AND, [child, criterion]);
      }

      return { ...child, children: [...child.children, criterion] };
    }),
  };
};

export const appendConditionGroup = ({
  root,
  criterion,
}: AppendConditionGroupParams): BusinessPolicyConditionNodeDTO => {
  if (!isComparisonNode(root) && root.operator === BusinessPolicyAstLogicalOperator.OR) {
    return { ...root, children: [...root.children, criterion] };
  }

  return createLogicalNode(BusinessPolicyAstLogicalOperator.OR, [root, criterion]);
};

const collapseSingleChild = (node: BusinessPolicyLogicalNodeDTO) =>
  node.children.length === 1 ? node.children[0] : node;

export const removeCriterionAtPath = (
  root: BusinessPolicyConditionNodeDTO,
  path: ConditionNodePath,
): BusinessPolicyConditionNodeDTO | null => {
  if (!path.length) {
    return null;
  }

  if (isComparisonNode(root)) {
    return root;
  }

  const [index, ...rest] = path;
  const { children } = root;
  const child = children[index];

  if (!child) {
    return root;
  }

  const nextChild = rest.length ? removeCriterionAtPath(child, rest) : null;
  const nextChildren = nextChild
    ? children.map((current, childIndex) => (childIndex === index ? nextChild : current))
    : children.filter((_, childIndex) => childIndex !== index);

  if (!nextChildren.length) {
    return null;
  }

  return collapseSingleChild({ ...root, children: nextChildren });
};

export const getNodeMeasure = (
  node: BusinessPolicyComparisonNodeDTO | null,
  slot: BusinessPolicyNodeSlot,
) => {
  if (!node) {
    return null;
  }

  return slot === BusinessPolicyMeasureSlot.RIGHT ? node.rightMeasure : node.left;
};

export const getNodeMeasureKey = (
  node: BusinessPolicyComparisonNodeDTO | null,
  slot: BusinessPolicyNodeSlot,
) => {
  const measure = getNodeMeasure(node, slot);

  return measure ? findMeasureKey(measure) : null;
};

const withMeasure = (
  node: BusinessPolicyComparisonNodeDTO,
  slot: BusinessPolicyNodeSlot,
  update: (measure: BusinessPolicyMeasureDTO) => BusinessPolicyMeasureDTO,
): BusinessPolicyComparisonNodeDTO => {
  if (slot === BusinessPolicyMeasureSlot.LEFT) {
    return { ...node, left: update(node.left) };
  }

  return node.rightMeasure ? { ...node, rightMeasure: update(node.rightMeasure) } : node;
};

export const setNodeMeasure = ({ node, slot, measureKey }: SetNodeMeasureParams) => {
  const measure = buildMeasure(measureKey);

  if (slot === BusinessPolicyMeasureSlot.RIGHT) {
    return { ...node, rightMeasure: measure };
  }

  return { ...node, left: measure };
};

export const upsertNodeScope = ({ node, slot, scope }: UpsertNodeScopeParams) =>
  withMeasure(node, slot, (measure) => ({
    ...measure,
    scopes: [...measure.scopes.filter((current) => current.dimension !== scope.dimension), scope],
  }));

export const removeNodeScope = ({ node, slot, dimension }: RemoveNodeScopeParams) =>
  withMeasure(node, slot, (measure) => ({
    ...measure,
    scopes: measure.scopes.filter((current) => current.dimension !== dimension),
  }));

export const setNodeOrderHistory = ({ node, slot, orderHistory }: SetNodeOrderHistoryParams) =>
  withMeasure(node, slot, (measure) => ({
    ...measure,
    source: orderHistory
      ? BusinessPolicyMeasureSource.ORDER_HISTORY
      : BusinessPolicyMeasureSource.CURRENT_ORDER,
    orderHistory,
  }));

export const setNodePriceCompositions = ({
  node,
  slot,
  priceCompositionIds,
}: SetNodePriceCompositionsParams) =>
  withMeasure(node, slot, (measure) => ({ ...measure, priceCompositionIds }));

const getRestrictiveZeroComparison = (grain: BusinessPolicyGrain, isValidated: boolean) => {
  if (grain === BusinessPolicyGrain.ORDER) {
    return isValidated ? BusinessPolicyZeroComparison.EVALUATE : BusinessPolicyZeroComparison.FAIL;
  }

  return isValidated ? BusinessPolicyZeroComparison.PASS : BusinessPolicyZeroComparison.EVALUATE;
};

export const applyNodeZeroComparison = ({
  node,
  type,
  isValidateZeroTotalValue,
}: NodeZeroComparisonParams): BusinessPolicyComparisonNodeDTO => {
  if (!RESTRICTIVE_TYPES.includes(type)) {
    return { ...node, zeroComparison: BusinessPolicyZeroComparison.EVALUATE };
  }

  const { grain } = node.left;

  return {
    ...node,
    zeroComparison: getRestrictiveZeroComparison(grain, isValidateZeroTotalValue),
    includeAbsentGroups: isValidateZeroTotalValue && grain === BusinessPolicyGrain.PRODUCT_COLOR,
  };
};

export const readNodeZeroComparison = ({ node, type }: ReadNodeZeroComparisonParams) => {
  if (!RESTRICTIVE_TYPES.includes(type)) {
    return true;
  }

  if (node.left.grain === BusinessPolicyGrain.ORDER) {
    return node.zeroComparison !== BusinessPolicyZeroComparison.FAIL;
  }

  return node.zeroComparison === BusinessPolicyZeroComparison.PASS;
};

export const applyNodeUnmatchedHandling = (
  node: BusinessPolicyComparisonNodeDTO,
  isValidateUnmatchedValues: boolean,
): BusinessPolicyComparisonNodeDTO => ({
  ...node,
  unmatchedHandling: isValidateUnmatchedValues
    ? BusinessPolicyUnmatchedHandling.BOTH
    : BusinessPolicyUnmatchedHandling.NONE,
});

export const readNodeUnmatchedHandling = (node: BusinessPolicyComparisonNodeDTO) =>
  node.unmatchedHandling !== BusinessPolicyUnmatchedHandling.NONE;
